// components/ConfirmEmail.jsx
import React, { useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import robotWhite from "../images/codiii-icon_white.svg";
import robotColor from "../images/codiii-icon.svg";
import mascot from "../images/codiii-mascot.svg";

// --- reuse the same primitives as SignIn/SignUp ---
const PageWrap = styled.div`
  * { margin:0; padding:0; box-sizing:border-box; }
  min-height:100vh; display:flex; align-items:center; justify-content:center;
  position:relative; overflow:hidden;
  font-family:'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif;
  background:linear-gradient(135deg,#f97316 0%,#ea580c 50%,#dc2626 100%);
`;
const RobotPattern = styled.div`
  position:absolute; inset:0; pointer-events:none; z-index:1;
  .robot-icon { position:absolute; width:80px; height:80px; opacity:.1; animation:float 20s infinite linear; }
  .robot-icon:nth-child(odd){ animation-direction:reverse; opacity:.08; }
  .robot-icon:nth-child(1){ top:10%; left:5%; animation-delay:0s; }
  .robot-icon:nth-child(2){ top:20%; right:8%; animation-delay:-3s; }
  .robot-icon:nth-child(3){ top:40%; left:12%; animation-delay:-6s; }
  .robot-icon:nth-child(4){ top:60%; right:15%; animation-delay:-9s; }
  .robot-icon:nth-child(5){ top:75%; left:20%; animation-delay:-12s; }
  .robot-icon:nth-child(6){ top:30%; left:50%; animation-delay:-15s; }
  .robot-icon:nth-child(7){ top:80%; right:30%; animation-delay:-18s; }
  .robot-icon:nth-child(8){ top:15%; left:75%; animation-delay:-2s; }
  @keyframes float{
    0%{transform:translateY(0) rotate(0)}
    25%{transform:translateY(-20px) rotate(90deg)}
    50%{transform:translateY(-40px) rotate(180deg)}
    75%{transform:translateY(-20px) rotate(270deg)}
    100%{transform:translateY(0) rotate(360deg)}
  }
  img{ width:100%; height:100%; object-fit:contain; filter:drop-shadow(0 2px 8px rgba(0,0,0,.1)); }
`;
const Card = styled.div`
  position:relative; z-index:5; width:100%; max-width:440px;
  background:rgba(255,255,255,.95); backdrop-filter:blur(20px);
  border-radius:24px; padding:48px;
  box-shadow:0 25px 50px rgba(0,0,0,.25), 0 0 0 1px rgba(255,255,255,.1);
  animation:slideUp .8s ease-out;
  @keyframes slideUp{ from{opacity:0; transform:translateY(30px) scale(.95)} to{opacity:1; transform:translateY(0) scale(1)} }
  @media (max-width:480px){ margin:20px; padding:32px 24px; }
`;
const LogoSection = styled.div`
  text-align:center; margin-bottom:40px;
  .logo-container{ display:inline-flex; align-items:center; justify-content:center; margin-bottom:16px; }
  .logo{ width:64px; height:64px; margin-right:12px; }
  .brand-text{ font-size:32px; font-weight:800; color:#1f2937; letter-spacing:-.02em; }
  .brand-dots{ color:#f97316; }
  .tagline{ color:#6b7280; font-size:14px; font-weight:500; text-transform:uppercase; letter-spacing:.5px; margin-top:8px; }
`;
const H1 = styled.h1` font-size:28px; font-weight:700; color:#1f2937; margin-bottom:8px; text-align:center; `;
const Subtitle = styled.p` color:#6b7280; font-size:16px; text-align:center; margin-bottom:24px; line-height:1.5; `;
const FormGroup = styled.div` margin-bottom:24px; `;
const Label = styled.label` display:block; color:#374151; font-weight:600; font-size:14px; margin-bottom:8px; `;
const Input = styled.input`
  width:100%; padding:16px 20px; border:2px solid #e5e7eb; border-radius:16px; font-size:16px; background:rgba(255,255,255,.9);
  transition:all .3s ease;
  &:focus{ outline:none; border-color:#f97316; box-shadow:0 0 0 4px rgba(249,115,22,.1), 0 4px 20px rgba(0,0,0,.1); background:#fff; }
  &::placeholder{ color:#9ca3af; }
`;
const PrimaryBtn = styled.button`
  width:100%; background:linear-gradient(135deg,#f97316 0%, #ea580c 100%);
  border:none; border-radius:16px; color:#fff; padding:18px 24px; font-size:16px; font-weight:700; cursor:pointer; transition:all .3s ease;
  box-shadow:0 8px 20px rgba(249,115,22,.3), 0 2px 8px rgba(0,0,0,.1);
  position:relative; overflow:hidden; margin-top:4px;
  &:before{ content:''; position:absolute; top:0; left:-100%; width:100%; height:100%;
    background:linear-gradient(90deg,transparent,rgba(255,255,255,.2),transparent); transition:left .5s ease; }
  &:hover:before{ left:100%; }
`;
const MutedBtn = styled(PrimaryBtn)` background:linear-gradient(135deg,#6b7280 0%,#4b5563 100%); `;
const SuccessBox = styled.div`
  color:#065f46;
  background:rgba(16,185,129,0.12);
  border:1px solid rgba(16,185,129,0.28);
  padding:12px 14px; border-radius:12px; margin:12px 0;
  font-size:14px; font-weight:600;
`;


async function parseJsonSafe(resp){
  const ct = resp.headers.get('content-type') || '';
  const txt = await resp.text();
  if (ct.includes('application/json') && txt) { try { return JSON.parse(txt); } catch {} }
  return txt || null;
}

export default function ConfirmEmail(){
  const nav = useNavigate();
  const { state, search } = useLocation();
  const qs = new URLSearchParams(search);

  const [email, setEmail] = useState(state?.email || qs.get('email') || '');
  const [code, setCode]   = useState(qs.get('code') || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
  const [resending, setResending] = useState(false);
  const [resentOk, setResentOk]   = useState(false);
  const [cooldown, setCooldown]   = useState(0);


  async function onConfirm(e){
    e.preventDefault();
    setError('');
    if (!email || !code) { setError('Enter the 6-digit code we emailed you.'); return; }
    setSubmitting(true);
    try{
      const r = await fetch(`${API_BASE}/api/auth/confirm`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email, code })
      });
      const data = await parseJsonSafe(r);
      if (!r.ok) throw new Error(typeof data==='string'?data:(data?.detail||'Confirmation failed'));
      nav('/auth/signin', { replace:true });
    }catch(err){ setError(err.message || String(err)); }
    finally{ setSubmitting(false); }
  }

  async function onResend(e){
    e.preventDefault();
    if (!email) { setError('Enter your email first.'); return; }
    if (resending || cooldown > 0) return;

    setError('');
    setResending(true);
    setResentOk(false);

    try{
      const r = await fetch(`${API_BASE}/api/auth/resend-confirmation`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email })
      });
      const data = await parseJsonSafe(r);
      if (!r.ok) throw new Error(typeof data==='string' ? data : (data?.detail || 'Couldn’t resend code'));

      setResentOk(true);       // ✅ show success note
      startCooldown(30);       // throttle clicks
    }catch(err){
      setError(err.message || String(err));
    }finally{
      setResending(false);
    }
  }


  function startCooldown(sec = 30){
    setCooldown(sec);
    const id = setInterval(() => {
      setCooldown(s => {
        if (s <= 1) { clearInterval(id); return 0; }
        return s - 1;
      });
    }, 1000);
  }

  return (
    <PageWrap>
      <RobotPattern>
        {[1,2,3,4,5,6,7,8].map(i=>(
          <div key={i} className="robot-icon">
            <img src={i<=5?robotColor:robotWhite} alt="CODiii Robot" />
          </div>
        ))}
      </RobotPattern>

      <Card>
        <LogoSection>
          <div className="logo-container">
            <div className="logo"><img src={mascot} alt="CODiii Logo" /></div>
            <span className="brand-text">COD<span className="brand-dots">iii</span></span>
          </div>
          <div className="tagline">The Design & Construction Brain</div>
        </LogoSection>

        <H1>Confirm your email</H1>
        <Subtitle>
          We sent a 6-digit code to <b>{email || 'your email'}</b>. Enter it below.
        </Subtitle>

        <form onSubmit={onConfirm}>
          {!state?.email && (
            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email}
                onChange={e=>{ setEmail(e.target.value); setResentOk(false);}} placeholder="you@domain.com" required />
            </FormGroup>
          )}

          <FormGroup>
            <Label htmlFor="code">6-digit code</Label>
            <Input id="code" type="text" inputMode="numeric" pattern="\d*" maxLength={6}
              value={code} onChange={e=>setCode(e.target.value)} placeholder="Enter code" required />
          </FormGroup>

          {error && (
            <div style={{
              color:'#b91c1c', background:'rgba(185,28,28,0.08)',
              border:'1px solid rgba(185,28,28,0.25)', padding:'12px 14px',
              borderRadius:12, marginBottom:16
            }}>
              {error}
            </div>
          )}
          {resentOk && (
            <SuccessBox>
              Code sent! Check your inbox.
              {cooldown > 0 && <> You can resend in {cooldown}s.</>}
            </SuccessBox>
          )}


          <PrimaryBtn type="submit" disabled={submitting}>
            {submitting ? 'Verifying…' : 'Verify email'}
          </PrimaryBtn>
          <MutedBtn type="button" onClick={()=>nav('/auth/signin')} style={{marginTop:8}}>
            Back to Sign In
          </MutedBtn>

         <div style={{textAlign:'center', marginTop:16}}>
            Didn't get a code?{' '}
            <a
              href="#"
              onClick={onResend}
              style={{ pointerEvents:(resending||cooldown>0)?'none':'auto', opacity:(resending||cooldown>0)?0.6:1 }}
            >
              {resending ? 'Sending…' : (cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend')}
            </a>
          </div>

        </form>
      </Card>
    </PageWrap>
  );
}
